// src/pages/About.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Shield, Heart } from 'lucide-react';
import { team } from '../data/team';
import { faqs } from '../data/faq';
import { staggerContainer, fadeInUp, fadeInLeft, fadeInRight } from '../utils/animations';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const About: React.FC = () => {
  const { ref: storyRef, controls: storyControls } = useScrollAnimation();
  const { ref: teamRef, controls: teamControls } = useScrollAnimation();
  const { ref: valuesRef, controls: valuesControls } = useScrollAnimation();

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-mauve-50 to-white pt-24 pb-16">
        <div className="container-custom text-center">
          <h1 className="text-5xl font-bold text-mauve-900 mb-6">
            About GetNailedbyAJ
          </h1>
          <p className="text-xl text-mauve-700 max-w-3xl mx-auto">
            Where traditional Ghanaian beauty meets modern nail artistry in the heart of Accra
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section ref={storyRef} className="section-padding bg-white">
        <motion.div
          initial="initial"
          animate={storyControls}
          variants={staggerContainer}
          className="container-custom"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInLeft}>
              <h2 className="text-4xl font-bold text-mauve-900 mb-6">
                Our Ghanaian Story
              </h2>
              <div className="space-y-4 text-mauve-700">
                <p>
                  Founded in 2020 by Sandra Appiah, Get Nailed by AJ began as a passion project 
                  to bring world class nail care to Accra while celebrating Ghana's rich 
                  cultural heritage.
                </p>
                <p>
                  After training in London and working with top salons internationally, 
                  AJ returned to Ghana with a vision: to create a nail salon that combines 
                  international standards with authentic Ghanaian ingredients and designs.
                </p>
                <p>
                  Today, we're proud to serve professionals, brides, and beauty enthusiasts 
                  across Greater Accra, bringing luxury nail care with a local touch.
                </p>
              </div>
            </motion.div>
            
            <motion.div 
              variants={fadeInRight}
              className="bg-gradient-to-br from-mauve-300 to-mauve-500 rounded-2xl p-8 h-full min-h-[300px] flex items-center justify-center"
            >
              <div className="text-white text-center">
                <h3 className="text-2xl font-bold mb-4">5+ Years of Excellence</h3>
                <p className="text-lg">
                  Serving Accra with premium nail care since 2020
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Our Values */}
      <section ref={valuesRef} className="section-padding bg-mauve-50">
        <motion.div
          initial="initial"
          animate={valuesControls}
          variants={staggerContainer}
          className="container-custom"
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-mauve-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-mauve-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Shield size={32} />,
                title: 'Hygiene & Safety',
                description: 'Hospital-grade sterilization and single-use tools for every client'
              },
              {
                icon: <Heart size={32} />,
                title: 'Local Ingredients',
                description: 'Authentic Ghanaian ingredients, gel and coconut oil in our treatments'
              },
              {
                icon: <Award size={32} />,
                title: 'Professional Excellence',
                description: 'Internationally trained technicians with continuous education'
              },
              {
                icon: <Users size={32} />,
                title: 'Community Focus',
                description: 'Supporting local suppliers and training young nail artists in Accra'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                custom={index}
                className="bg-white rounded-2xl p-6 shadow-lg border border-mauve-100 hover:shadow-xl transition-shadow"
              >
                <div className="text-mauve-500 mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-mauve-900 mb-3">{value.title}</h3>
                <p className="text-mauve-700">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Meet the Team */}
      <section ref={teamRef} className="section-padding bg-white">
        <motion.div
          initial="initial"
          animate={teamControls}
          variants={staggerContainer}
          className="container-custom"
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-mauve-900 mb-4">
              Meet Our Expert Team
            </h2>
            <p className="text-lg text-mauve-600 max-w-2xl mx-auto">
              Certified professionals dedicated to your nail care experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.id}
                variants={fadeInUp}
                custom={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-mauve-100 hover:shadow-xl transition-shadow"
              >
                <div className="bg-gradient-to-r from-mauve-400 to-mauve-500 p-8 text-white text-center">
                  <h3 className="text-2xl font-bold">{member.name}</h3>
                  <p className="opacity-90">{member.role}</p>
                </div>
                
                <div className="p-6">
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm text-mauve-600 mb-2">
                      <span className="font-medium">Experience</span>
                      <span>{member.experience}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-mauve-600">
                      <span className="font-medium">Certification</span>
                      <span className="text-right">{member.certification}</span>
                    </div>
                  </div>
                  
                  <p className="text-mauve-700 mb-6">{member.bio}</p>
                  
                  <div>
                    <h4 className="font-bold text-mauve-800 mb-3">Specialties</h4>
                    <div className="flex flex-wrap gap-2">
                      {member.specialties.map((specialty) => (
                        <span
                          key={specialty}
                          className="px-3 py-1 bg-mauve-100 text-mauve-700 rounded-full text-sm"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-mauve-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-mauve-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-mauve-600 max-w-2xl mx-auto">
              Common questions about our services in Accra
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow border border-mauve-100"
              >
                <h3 className="text-lg font-bold text-mauve-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-mauve-700">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.main>
  );
};

export default About;